import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useHub } from "../lib/dados";
import {
  anotarAula,
  concluirAula,
  lerAulasDoBonus,
  mensagemDaEscrita,
  type AulaBonus,
  type CursoBonus as Curso,
} from "../lib/cursoBonus";
import {
  duracaoEmTexto,
  duracaoTotalEmTexto,
  porSecao,
  progressoDe,
  proximaAula,
  rotuloDaAcao,
} from "../lib/aulasBonus";
import { mensagemDaAbertura, type MotivoAbertura } from "../lib/abrir";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import {
  Anotacao,
  CursoConcluido,
  FaixaDeProgresso,
  LinhaDaAula,
  Modulo,
  Tique,
} from "../componentes/CursoBonusUi";
import { IconeExterno } from "../componentes/ui";
import Carregando from "../componentes/Carregando";
import { Aviso } from "../componentes/ui";

// A tela de um curso bônus da Pós Finanças (000177), com o progresso do aluno
// (000183): concluir, anotar, continuar de onde parou.
//
// ---------------------------------------------------------------------------
// Uma tela, três cards — o card é o PARÂMETRO
// ---------------------------------------------------------------------------
// `excel-financas`, `sheets-financas` e `ia-financas` mostram a mesma coisa com
// dados diferentes, então são um componente só, como `FerramentaDeConteudo` é
// para o IR Pro e o MEI Pro. Três telas iguais seriam três lugares para
// consertar o mesmo defeito.
//
// ---------------------------------------------------------------------------
// O estado das aulas mora AQUI, e as peças o recebem
// ---------------------------------------------------------------------------
// O progresso aparece em quatro lugares da mesma tela — a faixa da cabine, o
// tile "Concluídas", o "N de M" de cada módulo e o tique de cada linha. É a
// lição do Leitor de Processos, onde o mesmo contador em três lugares mostrava
// "0 de 3" no alto e "1 de 3" embaixo: estado que vários componentes leem mora
// no PAI, e um tique atualiza a lista inteira sem ida à rede — o servidor já
// respondeu com a data que gravou.
//
// ---------------------------------------------------------------------------
// Cada aula é um LINK, e o destino é a ponte
// ---------------------------------------------------------------------------
// Nada de `onClick` que chama a Edge Function e depois abre: as três razões
// medidas estão em `Abrir.tsx` (bloqueador de pop-up depois do `await`,
// `window.opener` numa aba de terceiro, e clique que parece não funcionar
// enquanto a rede responde). Com `/abrir/<curso>/<card>/<aula>`, ctrl+clique e
// "abrir em nova aba" voltam a funcionar, e o aluno vê o loader da marca.
//
// `target="_blank" rel="noreferrer"` porque a aula é OUTRO sistema — é a mesma
// regra do `LinkDoCard` para destino de fora, e o `sr-only` "abre em outra aba"
// existe por isso.
//
// ---------------------------------------------------------------------------
// O que a tela NÃO faz
// ---------------------------------------------------------------------------
// Não decide acesso (o banco decide, em `bonus_minhas_aulas` e nas duas RPCs de
// escrita), não ordena as aulas (a ordem vem do banco, com desempate estável),
// não numera pela `ordem` da fonte (ver `porSecao`) e não sabe se o vídeo foi
// assistido: "concluída" é a marca do ALUNO, e o Memberkit tem a própria, que
// não se conversa com esta — a nota no fim da lista diz isso com letra. E não
// oferece o download do material de apoio: ele é servido pelo Memberkit, dentro
// da aula, onde a autorização é deles.

export default function CursoBonus({ card }: { card: string }) {
  const [params] = useSearchParams();
  const hub = useHub();
  const [estado, setEstado] = useState<
    { fase: "carregando" } | { fase: "pronto"; curso: Curso } | { fase: "recusado"; motivo: string } | { fase: "erro" }
  >({ fase: "carregando" });
  // Os tiques em voo e os que não gravaram — por aula, para o erro ficar NA
  // LINHA e não no lugar da tela.
  const [ocupadas, setOcupadas] = useState<Set<number>>(new Set());
  const [erros, setErros] = useState<Map<number, string>>(new Map());
  // Uma anotação aberta por vez: a tela é uma lista, e três painéis abertos a
  // transformariam em formulário.
  const [anotacaoAberta, setAnotacaoAberta] = useState<number | null>(null);

  // O curso vem da query que `aberturaDoCard` põe no link. Sem ela (rota
  // digitada, link antigo, favorito), o card pertence a um curso só e é ele —
  // a mesma precedência de `cursoDeVolta`, e pela mesma razão: mandar o curso
  // errado é pior que não adivinhar.
  const daQuery = params.get("curso");
  const doCard =
    hub.fase === "pronto"
      ? hub.hub.cardsDeCurso.find((c) => c.card_slug === card)?.por_cursos ?? []
      : [];
  const curso = daQuery ?? (doCard.length === 1 ? doCard[0] : null);

  useEffect(() => {
    if (hub.fase !== "pronto") return;
    if (!curso) {
      setEstado({ fase: "recusado", motivo: "sem-acesso" });
      return;
    }
    let vivo = true;
    (async () => {
      const r = await lerAulasDoBonus(card, curso);
      if (!vivo) return;
      setEstado(r.fase === "pronto" ? { fase: "pronto", curso: r.curso } : r);
    })();
    return () => {
      vivo = false;
    };
  }, [card, curso, hub.fase]);

  /** Troca UMA aula na lista, sem ida à rede: o servidor já disse o que gravou. */
  function atualizarAula(aulaId: number, mudanca: Partial<AulaBonus>) {
    setEstado((e) =>
      e.fase !== "pronto"
        ? e
        : {
            ...e,
            curso: {
              ...e.curso,
              aulas: e.curso.aulas.map((a) => (a.aulaId === aulaId ? { ...a, ...mudanca } : a)),
            },
          },
    );
  }

  async function concluir(aula: AulaBonus, valor: boolean) {
    if (!curso) return;
    setOcupadas((s) => new Set(s).add(aula.aulaId));
    setErros((m) => {
      const n = new Map(m);
      n.delete(aula.aulaId);
      return n;
    });
    const r = await concluirAula(card, curso, aula.aulaId, valor);
    setOcupadas((s) => {
      const n = new Set(s);
      n.delete(aula.aulaId);
      return n;
    });
    if (r.ok) {
      // A data é a do SERVIDOR, e marcar o que já estava marcado devolve a data
      // antiga — a RPC é idempotente por estado.
      atualizarAula(aula.aulaId, { concluidaEm: r.concluidaEm });
    } else {
      // Erro de ESCRITA não derruba a tela: a linha informa, o resto fica.
      setErros((m) => new Map(m).set(aula.aulaId, mensagemDaEscrita(r.motivo)));
    }
  }

  async function anotar(aula: AulaBonus, texto: string): Promise<string | null> {
    if (!curso) return "sem curso";
    const r = await anotarAula(card, curso, aula.aulaId, texto);
    if (r.ok) {
      atualizarAula(aula.aulaId, { anotacao: r.anotacao });
      return null;
    }
    return mensagemDaEscrita(r.motivo);
  }

  if (hub.fase === "carregando" || estado.fase === "carregando") {
    return <Carregando texto="Carregando as aulas…" />;
  }

  if (estado.fase === "recusado" || estado.fase === "erro") {
    // Erro de LEITURA é diferente de recusa: sem leitura não há tela, e a
    // mensagem tem de dizer qual dos dois é. A recusa vem com motivo do banco e
    // `mensagemDaAbertura` já sabe traduzir cada um — inclusive "falta assinar o
    // contrato", que é a única acionável pelo aluno.
    const { titulo, texto } =
      estado.fase === "erro"
        ? {
            titulo: "Não conseguimos carregar as aulas",
            texto: "O problema é nosso, não seu. Tente de novo em alguns minutos.",
          }
        : mensagemDaAbertura(estado.motivo as MotivoAbertura);
    return (
      <Aviso
        titulo={titulo}
        acao={
          <Link to={curso ? `/curso/${curso}` : "/"} className="pill-cta">
            {curso ? "Voltar ao curso" : "Voltar ao início"}
          </Link>
        }
      >
        {texto}
      </Aviso>
    );
  }

  const { aulas, nomeNaFonte } = estado.curso;
  const cards = hub.fase === "pronto" ? hub.hub.cardsDeCurso : [];
  const nomeDoCard = cards.find((c) => c.card_slug === card)?.card_nome ?? nomeNaFonte;
  // Os OUTROS cursos bônus do mesmo curso: a única sugestão da tela, e ela é
  // dado (os cards que o aluno tem), não recomendação inventada.
  const irmaos = cards
    .filter((c) => c.card_tipo === "curso_bonus" && c.card_slug !== card && (curso ? c.por_cursos.includes(curso) : false))
    .map((c) => ({ slug: c.card_slug, nome: c.card_nome }));

  const progresso = progressoDe(aulas);
  const proxima = proximaAula(aulas);
  const rotulo = rotuloDaAcao(aulas, proxima);
  const total = duracaoTotalEmTexto(aulas);
  // "Faltam" só no meio do caminho: no começo é igual ao total, no fim é zero, e
  // nos dois o tile seria ruído.
  const restante =
    progresso.concluidas > 0 && !progresso.concluido && (progresso.restanteSegundos ?? 0) > 0
      ? duracaoEmTexto(progresso.restanteSegundos)
      : null;
  const comMaterial = aulas.filter((a) => a.temMaterial).length;
  const grupos = porSecao(aulas);
  // `curso` nulo não deveria chegar aqui (a tela recusa antes), mas o link não
  // pode ser montado com "null" no caminho — a ponte responderia sem saber qual
  // escola atende.
  const ponte = (a: AulaBonus) =>
    curso
      ? `/abrir/${encodeURIComponent(curso)}/${encodeURIComponent(card)}/${a.aulaId}`
      : null;
  // A numeração é contínua ao longo do curso, atravessando as seções — é assim
  // que o aluno conta "estou na aula 7 de 12". Ela sai da POSIÇÃO na lista, e
  // nunca de `a.ordem`: a `position` do Memberkit tem buracos onde aulas foram
  // removidas (o Excel vai de 1 a 13 com 12 aulas), e mostrá-la escreveria "13."
  // num curso de doze.
  let n = 0;

  // A descrição NÃO repete número: "3 de 12", "3 h 28" e "faltam 2 h 37" já
  // estão nos tiles e na faixa logo abaixo. A primeira captura desta versão
  // mostrava o mesmo "3 de 12" três vezes na mesma cabine — é a cicatriz do
  // "índice em dois lugares na mesma tela não é redundância inofensiva". A
  // frase diz o que os números não dizem: o que acontece ao clicar.
  const descricao = progresso.concluido ? (
    <>
      Você concluiu este curso. As aulas continuam abertas para rever quando quiser, com você
      já conectado — sem senha e sem segunda tela de login.
    </>
  ) : progresso.concluidas > 0 ? (
    <>
      Continue de onde parou: as aulas abrem na plataforma de vídeo com você já conectado, sem
      senha. Marque cada uma como concluída e o progresso fica guardado aqui.
    </>
  ) : (
    <>
      As aulas abrem na plataforma de vídeo com você já conectado — sem senha e sem segunda
      tela de login. Marque cada uma como concluída para acompanhar o seu progresso aqui.
    </>
  );

  const destinoDaProxima = proxima ? ponte(proxima) : null;
  const acao =
    proxima && rotulo && destinoDaProxima ? (
      <div className="flex flex-wrap items-center gap-3">
        <a href={destinoDaProxima} target="_blank" rel="noreferrer" data-continuar className="pill-cta">
          {rotulo}
          <IconeExterno className="size-4" />
        </a>
        {/* Sobre navy: `text-white` e `text-navy-mute`, nunca `tinta`/`cinza`. */}
        <span className="min-w-0 text-sm text-navy-mute">
          <span className="font-semibold text-white">{proxima.titulo}</span>
          {duracaoEmTexto(proxima.duracaoSegundos) ? ` · ${duracaoEmTexto(proxima.duracaoSegundos)}` : ""}
        </span>
      </div>
    ) : progresso.concluido ? (
      // Só o selo. A primeira versão punha aqui um botão "Próximo: Sheets…", e o
      // painel de curso concluído, logo abaixo, já lista os outros cursos bônus —
      // eram os mesmos links a cem pixels de distância.
      <span
        data-selo-concluido
        className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold text-white"
      >
        <Tique className="size-4 text-laranja-claro" />
        Curso concluído
      </span>
    ) : undefined;

  return (
    <main className="mx-auto w-full max-w-6xl px-4 pt-6 pb-12 sm:px-6">
      <Cabine
        // SEM `trilha`, de propósito. A primeira versão punha "Voltar ao curso"
        // aqui, e a captura mostrou o defeito: a `BarraDeVolta` da `Moldura` já
        // desenha "← Voltar para Pós-graduação em Finanças e Controladoria"
        // cinquenta pixels acima — dois caminhos para a mesma ação, colados. É a
        // cicatriz do "índice em dois lugares na mesma tela não é redundância
        // inofensiva", e o lugar do voltar é um só desde 26/08 (o pedido de
        // Victor que criou a `BarraDeVolta`). A `trilha` da `Cabine` serve para
        // hierarquia INTERNA — "MEI PRO ›" numa sub-tela —, e esta tela é a raiz
        // da ferramenta.
        rotulo="Curso bônus"
        titulo={nomeDoCard}
        descricao={descricao}
        acao={acao}
        fatos={
          progresso.total > 0 ? (
            <>
              {/* Tile de fato só existe com dado medido — e o progresso É dado
                  medido: o aluno o criou com um clique. "Nada de progresso" no
                  CLAUDE.md é sobre dado que não existe (ver a 000183). */}
              <TileDeFato
                rotulo="Concluídas"
                valor={`${progresso.concluidas} de ${progresso.total}`}
                apoio={
                  progresso.concluido
                    ? "curso concluído"
                    : progresso.concluidas === 0
                      ? "nenhuma ainda"
                      : `${progresso.porcentagem}% do curso`
                }
              />
              {/* Sem duração no catálogo, o tile não aparece — zero grande é ruído. */}
              {total && <TileDeFato rotulo="Duração" valor={total} apoio="somando os vídeos" />}
              {restante && <TileDeFato rotulo="Faltam" valor={restante} apoio="de vídeo por assistir" />}
              {comMaterial > 0 && (
                <TileDeFato
                  rotulo="Material de apoio"
                  valor={comMaterial}
                  apoio={comMaterial === 1 ? "aula com anexo" : "aulas com anexo"}
                />
              )}
            </>
          ) : undefined
        }
        rodape={progresso.total > 0 ? <FaixaDeProgresso aulas={aulas} progresso={progresso} /> : undefined}
      />

      {progresso.concluido && (
        <CursoConcluido
          nome={nomeDoCard}
          progresso={progresso}
          duracao={total}
          irmaos={irmaos}
          curso={curso}
        />
      )}

      {aulas.length === 0 ? (
        // Ausência precisa de texto próprio: uma lista vazia sem frase é lida
        // como tela quebrada. (A "linha que não aparece" do HUB DP.)
        <p className="surface mt-6 p-6 text-sm text-cinza">
          As aulas deste curso ainda não foram cadastradas aqui. O seu acesso está liberado —
          se demorar, o suporte resolve.
        </p>
      ) : (
        <>
          <div className="mt-6 space-y-5">
            {grupos.map((g, gi) => (
              <Modulo
                key={g.secao ?? `grupo-${gi}`}
                indice={gi + 1}
                // Curso de uma seção só vira um módulo chamado "Aulas", sem o
                // numeral: um único título de grupo repetindo o nome do curso é
                // ruído.
                titulo={grupos.length > 1 && g.secao ? g.secao : "Aulas"}
                mostrarIndice={grupos.length > 1}
                aulas={g.aulas}
              >
                {g.aulas.map((a) => {
                  n += 1;
                  const numero = n;
                  return (
                    <LinhaDaAula
                      key={a.aulaId}
                      aula={a}
                      numero={numero}
                      para={ponte(a)}
                      ehProxima={proxima?.aulaId === a.aulaId}
                      ocupado={ocupadas.has(a.aulaId)}
                      erro={erros.get(a.aulaId) ?? null}
                      anotacaoAberta={anotacaoAberta === a.aulaId}
                      onConcluir={(valor) => concluir(a, valor)}
                      onAlternarAnotacao={() =>
                        setAnotacaoAberta((atual) => (atual === a.aulaId ? null : a.aulaId))
                      }
                    >
                      <Anotacao
                        aulaId={a.aulaId}
                        numero={numero}
                        inicial={a.anotacao}
                        aberta={anotacaoAberta === a.aulaId}
                        onSalvar={(texto) => anotar(a, texto)}
                      />
                    </LinhaDaAula>
                  );
                })}
              </Modulo>
            ))}
          </div>
          {/* A verdade sobre o que a marca É: só o aluno sabe se assistiu. Sem
              esta linha, o aluno que marcou tudo aqui estranharia a plataforma de
              vídeo dizendo outra coisa. */}
          <p className="mt-6 text-xs leading-relaxed text-cinza">
            Marcar uma aula como concluída registra o seu progresso aqui no hub. A plataforma de
            vídeo tem a própria marcação, e as duas não se conversam.
          </p>
        </>
      )}
    </main>
  );
}
